import { computed, unref } from "vue";
import { useStore } from "vuex";

function toNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}

function sumAndFix(items, key) {
  if (items.length < 1) {
    return;
  }

  const total = items.reduce((sum, item) => {
    return sum + toNumber(item[key]);
  }, 0);

  return total.toFixed(5);
}

function buildMaterialItems({ wallsByType, selectedWallType, totalArea }) {
  const wallEntries = Object.entries(wallsByType ?? {});
  if (wallEntries.length < 1) {
    return [];
  }

  if (!selectedWallType) {
    const fallbackMaterials = wallEntries[0]?.[1];
    return Array.isArray(fallbackMaterials)
      ? fallbackMaterials.map((material) => {
        const materialPercentage = toNumber(material.material_pourcentage_coef);
        const materialThickness = toNumber(material.material_thickness_m);
        const density = toNumber(material.density_kg_m3);
        const carbonFootprintFactor = toNumber(material.carbon_footprint_kg_co2_kg);

        const area = toNumber(totalArea) * materialPercentage;
        const volume = (toNumber(totalArea) * materialThickness) * materialPercentage;
        const mass = volume * density;
        const footprint = mass * carbonFootprintFactor;

        return {
          name: material.display_name,
          area,
          volume,
          mass,
          footprint,
        };
      })
      : [];
  }

  let selectedMaterials = wallsByType?.[selectedWallType];
  if (!Array.isArray(selectedMaterials)) {
    const normalizedSelectedWallType = String(selectedWallType).trim().toLowerCase();
    const matchingEntry = wallEntries.find(([wallType]) => {
      return String(wallType).trim().toLowerCase() === normalizedSelectedWallType;
    });

    selectedMaterials = matchingEntry?.[1];
  }

  if (!Array.isArray(selectedMaterials)) {
    selectedMaterials = wallEntries[0]?.[1];
  }

  if (!Array.isArray(selectedMaterials)) {
    return [];
  }

  return selectedMaterials.map((material) => {
    const materialPercentage = toNumber(material.material_pourcentage_coef);
    const materialThickness = toNumber(material.material_thickness_m);
    const density = toNumber(material.density_kg_m3);
    const carbonFootprintFactor = toNumber(material.carbon_footprint_kg_co2_kg);

    const area = toNumber(totalArea) * materialPercentage;
    const volume = (toNumber(totalArea) * materialThickness) * materialPercentage;
    const mass = volume * density;
    const footprint = mass * carbonFootprintFactor;

    return {
      name: material.display_name,
      area,
      volume,
      mass,
      footprint,
    };
  });
}

export function useCarbonImpactTotals(options = {}) {
  const store = options.store || useStore();

  const scheduleTable = computed(() => {
    return store.state.scheduleTable;
  });

  const selectedWallType = computed(() => {
    if (options.selectedWallType !== undefined) {
      return unref(options.selectedWallType);
    }

    return scheduleTable.value?.selectedWallType;
  });

  const selectedTotalArea = computed(() => {
    if (options.totalArea !== undefined) {
      return toNumber(unref(options.totalArea));
    }

    return toNumber(store.state.customer?.totalArea);
  });

  const items = computed(() => {
    return buildMaterialItems({
      wallsByType: scheduleTable.value?.walls,
      selectedWallType: selectedWallType.value,
      totalArea: selectedTotalArea.value,
    });
  });

  const totalArea = computed(() => sumAndFix(items.value, "area"));
  const totalVolume = computed(() => sumAndFix(items.value, "volume"));
  const totalMass = computed(() => sumAndFix(items.value, "mass"));
  const totalFootprint = computed(() => sumAndFix(items.value, "footprint"));

  function buildCarbonImpactPayload(overrides = {}) {
    const totals = buildMaterialItems({
      wallsByType: scheduleTable.value?.walls,
      selectedWallType: overrides.selectedWallType ?? selectedWallType.value,
      totalArea: overrides.totalArea ?? selectedTotalArea.value,
    });

    return {
      materialtotals: {
        area: Number(sumAndFix(totals, "area") ?? 0),
        volume: Number(sumAndFix(totals, "volume") ?? 0),
        mass: Number(sumAndFix(totals, "mass") ?? 0),
        carbonFootprint: Number(sumAndFix(totals, "footprint") ?? 0),
      },
    };
  }

  return {
    items,
    totalArea,
    totalVolume,
    totalMass,
    totalFootprint,
    buildCarbonImpactPayload,
  };
}